import React from 'react';
import { Box } from '@indeed/ifl-components';

interface StickyActionFooterProps {
    children: React.ReactNode;
    /**
     * Max width of the centered content row. Match the owning flow's content
     * width so the buttons line up with the page content (defaults to the
     * Auto Apply flow's 450px).
     */
    maxInlineSize?: string;
}

/**
 * Sticky action footer shared across the Auto Apply flows: a viewport-fixed bar
 * pinned to the bottom of the screen with its content centered and capped to the
 * flow's content width. Used for the /auto-apply review Previous/Next controls and
 * the /auto-apply-setup floating primary CTA (progressive-automation bucket).
 *
 * IMPORTANT: render this as a sibling *outside* <PageTransition>. PageTransition
 * applies a transform, which would otherwise make `position: fixed` resolve
 * against its box instead of the viewport. The owning layout/page must also
 * reserve bottom padding so scrolled content clears the footer.
 */
export const StickyActionFooter: React.FC<StickyActionFooterProps> = ({
    children,
    maxInlineSize = '450px',
}) => (
    <Box
        sx={{
            position: 'fixed',
            insetBlockStart: 'auto',
            insetBlockEnd: 0,
            insetInlineStart: 0,
            insetInlineEnd: 0,
            inlineSize: '100%',
            display: 'flex',
            justifyContent: 'center',
            padding: 4,
            backgroundColor: 'background',
            boxShadow: 'mobilePopover',
        }}
    >
        <Box sx={{ display: 'flex', gap: 2, inlineSize: '100%', maxInlineSize }}>{children}</Box>
    </Box>
);

export default StickyActionFooter;
